import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';

const FAQSection = () => {
  const faqs = [
    {
      question: 'What is NOLA HVAC Connect?',
      answer:
        'NOLA HVAC Connect is a local resource designed to help homeowners better understand their heating and cooling systems before making repair or replacement decisions. The platform provides educational guides, HVAC pricing insights, and tools that help homeowners make informed decisions about their HVAC systems.',
    },
    {
      question: 'How does the Instant HVAC Estimate tool work?',
      answer:
        'The Instant HVAC Estimate tool helps homeowners understand general HVAC replacement cost ranges based on their home and system details. It provides helpful context before speaking with a contractor so homeowners can approach estimates with more confidence.',
    },
    {
      question: 'Can NOLA HVAC Connect help me decide whether to repair or replace my AC?',
      answer:
        'Yes. One of the most common questions homeowners face is whether repairing an HVAC system or replacing it makes more sense. NOLA HVAC Connect provides educational guides explaining system lifespan, common repair issues, and the factors that help homeowners evaluate their options.',
    },
    {
      question: 'Can NOLA HVAC Connect help me find a local HVAC contractor?',
      answer:
        'Yes. When homeowners are ready to move forward with a repair or replacement, NOLA HVAC Connect can help connect you with trusted HVAC professionals serving the New Orleans and Northshore region.',
    },
    {
      question: 'What areas does NOLA HVAC Connect serve?',
      answer:
        'NOLA HVAC Connect focuses on homeowners throughout the Greater New Orleans and Northshore region, including communities like New Orleans, Metairie, Kenner, Slidell, Mandeville, Covington, Hammond, and surrounding areas.',
    },
    {
      question: 'Where can I learn more about HVAC replacement costs?',
      answer:
        'HVAC replacement costs can vary depending on system size, equipment type, installation complexity, and home layout. NOLA HVAC Connect provides guides explaining the factors that influence HVAC installation pricing so homeowners know what to expect when comparing estimates.',
    },
    {
      question: 'Does NOLA HVAC Connect offer HVAC financing?',
      answer:
        'NOLA HVAC Connect does not provide financing directly, but many HVAC contractors offer financing options for both repairs and system replacements. The platform provides educational resources explaining how HVAC financing works and what homeowners should consider when evaluating payment options.',
    },
    {
      question: 'How often should HVAC systems be serviced?',
      answer:
        'Routine HVAC maintenance helps improve system efficiency, extend equipment lifespan, and reduce the chances of unexpected breakdowns. Many HVAC professionals recommend maintenance before peak cooling and heating seasons.',
    },
  ];

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is NOLA HVAC Connect?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NOLA HVAC Connect is a local resource that helps homeowners understand their heating and cooling systems before making repair or replacement decisions. The platform provides educational guides, pricing insights, and tools that help homeowners make informed HVAC decisions."
          }
        },
        {
          "@type": "Question",
          "name": "How does the Instant HVAC Estimate tool work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Instant HVAC Estimate tool helps homeowners understand typical HVAC replacement cost ranges based on their home and system information. It is designed to provide helpful context before speaking with a contractor."
          }
        },
        {
          "@type": "Question",
          "name": "Can NOLA HVAC Connect help me decide whether to repair or replace my AC?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. NOLA HVAC Connect provides educational guides explaining system lifespan, common repair issues, and the factors homeowners should consider when deciding whether to repair or replace an HVAC system."
          }
        },
        {
          "@type": "Question",
          "name": "Can NOLA HVAC Connect help me find a local HVAC contractor?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. When homeowners are ready, NOLA HVAC Connect can help connect them with trusted HVAC professionals serving the New Orleans and Northshore areas."
          }
        },
        {
          "@type": "Question",
          "name": "What areas does NOLA HVAC Connect serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NOLA HVAC Connect focuses on homeowners throughout the Greater New Orleans and Northshore region including New Orleans, Metairie, Kenner, Slidell, Mandeville, Covington, Hammond, and surrounding communities."
          }
        },
        {
          "@type": "Question",
          "name": "Where can I learn more about HVAC replacement costs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NOLA HVAC Connect provides an HVAC pricing guide that explains the factors influencing replacement costs including system size, equipment type, installation complexity, and home design."
          }
        },
        {
          "@type": "Question",
          "name": "Does NOLA HVAC Connect offer HVAC financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NOLA HVAC Connect does not provide financing directly but provides educational resources explaining HVAC financing options commonly offered by HVAC contractors."
          }
        },
        {
          "@type": "Question",
          "name": "How often should HVAC systems be serviced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Routine HVAC maintenance helps improve efficiency and prevent breakdowns. Many HVAC professionals recommend maintenance before peak cooling and heating seasons."
          }
        }
      ]
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-medium tracking-wide uppercase text-[#0EA5E9] mb-4">
            FAQ
          </p>
          <h2
            data-testid="faq-headline"
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Homeowners across <strong>New Orleans, Metairie, Kenner, Slidell, Mandeville, Covington, and the Northshore</strong> often have similar questions when researching HVAC repairs, system replacements, and maintenance.
          </p>
          <p className="text-base text-slate-500 mt-3">
            Below are quick answers explaining <strong>how NOLA HVAC Connect works and where you can find more information about your heating and cooling system.</strong>
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              data-testid={`faq-item-${index}`}
              className="bg-white rounded-2xl border border-slate-100 px-6 overflow-hidden"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${index}`}
                className="text-left text-[#0F172A] font-semibold hover:no-underline py-5"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${index}`}
                className="text-slate-600 leading-relaxed pb-5"
              >
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Still Have Questions CTA */}
        <div
          data-testid="faq-cta"
          className="mt-12 text-center bg-white p-8 rounded-3xl border border-slate-100"
        >
          <MessageCircle className="w-12 h-12 text-[#0EA5E9] mx-auto mb-4" />
          <h3
            className="text-xl font-bold text-[#0F172A] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Still Have Questions?
          </h3>
          <p className="text-slate-600 mb-6">
            Our team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="faq-contact-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-8 rounded-full orange-glow transition-all duration-300 transform hover:-translate-y-1"
            >
              Get Your Instant Estimate
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
